import type { VideoModel } from '..';
import {
    BaseVideoProvider,
    type VideoProviderAdapter,
    type VideoGenerateParams,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
    type ProviderContext,
} from '../base';
import { getValueByPath, isStatusMatch, hasImageInput, hasVideoInput } from '../tools';
import { translateToEnglish } from '../../shared/translate';
import { needsTranslation } from '../../image/tools';
import type {
    GeneralModelConfig,
    GeneralProviderOptions,
    GeneralStatusMapping,
    InputPayloadType,
} from './types';

export type {
    GeneralModelConfig,
    GeneralProviderOptions,
    GeneralStatusMapping,
    InputPayloadType,
    ModelInputBuilder,
    ApiPathBuilder,
    PollApiPathBuilder,
} from './types';

const REQUIRED_MAPPING_KEYS: (keyof GeneralStatusMapping)[] = [
    'taskIdPath',
    'statePath',
    'success',
    'failed',
    'processing',
    'videoUrlPath',
    'errorPath',
];

/**
 * 合并提供商默认状态映射与模型级覆盖，并校验完整性
 */
function resolveStatusMapping(
    providerName: string,
    modelId: string,
    base?: GeneralStatusMapping,
    override?: Partial<GeneralStatusMapping>
): GeneralStatusMapping {
    const merged = { ...base, ...override } as GeneralStatusMapping;
    const missing = REQUIRED_MAPPING_KEYS.filter((key) => merged[key] === undefined);
    if (missing.length > 0) {
        throw new Error(
            `[${providerName}][${modelId}] Incomplete status mapping, missing: ${missing.join(', ')}`
        );
    }
    return merged;
}

/**
 * 轮询路径占位符替换：
 * 支持 '${requestId}' / '{taskId}' / '{id}'，无占位符时追加 '/<taskId>'
 */
function resolvePollPath(pollApiPath: string, taskId: string): string {
    const placeholders = ['${requestId}', '{taskId}', '{id}'];
    for (const placeholder of placeholders) {
        if (pollApiPath.includes(placeholder)) {
            return pollApiPath.split(placeholder).join(taskId);
        }
    }
    return `${pollApiPath}/${taskId}`;
}

/**
 * 提取视频 URL 列表。
 * 兼容三种形态：字符串 / 字符串数组 / JSON 字符串（配合 videoUrlJsonPath 二次取值）
 */
function extractVideoUrls(data: any, mapping: GeneralStatusMapping): string[] {
    let raw = getValueByPath(data, mapping.videoUrlPath);
    if (mapping.videoUrlJsonPath !== undefined && typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            raw = getValueByPath(parsed, mapping.videoUrlJsonPath);
        } catch {
            return [];
        }
    }
    if (Array.isArray(raw)) {
        return raw.filter((u: any): u is string => typeof u === 'string' && !!u);
    }
    return typeof raw === 'string' && raw ? [raw] : [];
}

/**
 * 默认请求头：Bearer 鉴权
 */
function defaultHeaders(context: ProviderContext): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${context.apiKey}`,
    };
}

/**
 * 创建通用视频提供商（配置驱动，无需为每个渠道复制适配器代码）。
 *
 * 综合了各现有提供商的全部能力：
 * - 动态任务创建路径（frameMode / hasImage / hasVideo / duration / quality / modelUi）
 * - 轮询路径占位符（'{taskId}' / '{id}' / '${requestId}'）或自定义函数
 * - 提供商级状态映射 + 模型级局部覆盖
 * - failed 为空数组时按 errorPath 是否有值判断失败
 * - success 为空数组时按 videoUrlPath 是否取到 URL 判断成功（适配无状态字段的结果端点）
 * - 视频结果兼容 字符串 / 数组（多视频 videoUrls）/ JSON 字符串二次解析
 * - 自定义鉴权请求头（非 Bearer）与创建请求额外请求头
 * - 按模型覆盖 baseUrlEnv / apiKeyEnv（实例自动缓存）
 * - 提示词自动翻译（enableTranslation 或 UI translate_to_english 开启时）
 *
 * @example
 * const myProvider = createGeneralVideoProvider({
 *     providerName: 'MYPROVIDER',
 *     baseUrlEnv: 'MYPROVIDER_BASE_URL',
 *     apiKeyEnv: 'MYPROVIDER_API_KEY',
 *     statusMapping: {
 *         taskIdPath: 'data.id',
 *         statePath: 'data.status',
 *         success: ['completed'],
 *         failed: ['failed'],
 *         processing: ['queued', 'processing'],
 *         videoUrlPath: 'data.outputs[0]',
 *         errorPath: 'data.error',
 *     },
 *     models: {
 *         'my-model-1.0': {
 *             apiPath: '/api/v1/video/generate',
 *             pollApiPath: '/api/v1/video/tasks/{taskId}',
 *             buildInput: ({ prompt, duration, aspectRatio }) => ({
 *                 prompt,
 *                 duration,
 *                 aspect_ratio: aspectRatio,
 *             }),
 *         },
 *     },
 * });
 *
 * // 注册模型时：myProvider('my-model-1.0')
 */
export function createGeneralVideoProvider(
    options: GeneralProviderOptions
): (modelId: string) => VideoModel {
    const buildHeaders = options.buildHeaders ?? defaultHeaders;

    // 按 env 组合缓存 BaseVideoProvider 实例（支持模型级 env 覆盖）
    const providerCache = new Map<string, BaseVideoProvider>();
    const getOrCreateProvider = (baseUrlEnv: string, apiKeyEnv: string): BaseVideoProvider => {
        const cacheKey = `${baseUrlEnv}::${apiKeyEnv}`;
        let provider = providerCache.get(cacheKey);
        if (!provider) {
            provider = new BaseVideoProvider({
                baseUrlEnv,
                apiKeyEnv,
                debugEnv: options.debugEnv,
                providerName: options.providerName,
            });
            providerCache.set(cacheKey, provider);
        }
        return provider;
    };

    const getModelConfig = (modelId: string): GeneralModelConfig => {
        const config = options.models[modelId];
        if (!config) {
            throw new Error(`[${options.providerName}] Model ${modelId} not found`);
        }
        return config;
    };

    // 构建输入载荷（含可选提示词翻译）
    const buildInputPayload = async (
        modelId: string,
        config: GeneralModelConfig,
        params: VideoGenerateParams
    ): Promise<InputPayloadType> => {
        let prompt = params.prompt;
        const translateFromUi = (params.providerOptions as any)?.translate_to_english === true;
        if ((config.enableTranslation || translateFromUi) && needsTranslation(prompt)) {
            try {
                prompt = await translateToEnglish(prompt);
            } catch (error) {
                console.error(`[${options.providerName}][${modelId}] Failed to translate prompt:`, error);
            }
        }
        return config.buildInput({ ...params, prompt });
    };

    // 通用适配器
    const adapter: VideoProviderAdapter = {
        // 构建**任务创建**请求
        async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
            const config = getModelConfig(modelId);
            // 解析任务创建路径（字符串或动态构建器）
            const apiPath = typeof config.apiPath === 'string'
                ? config.apiPath
                : config.apiPath({
                    frameMode: params.modelUi?.frameMode,
                    hasImage: hasImageInput(params.imagePrompt),
                    hasVideo: hasVideoInput(params.videoUri),
                    duration: params.duration,
                    quality: params.quality,
                    modelUi: params.modelUi,
                    imagePrompt: params.imagePrompt,
                    params,
                });
            // 构建输入参数（可能是异步构建）
            const payload = await buildInputPayload(modelId, config, params);
            // 返回请求配置
            return {
                url: `${context.baseUrl}${apiPath}`,
                method: 'POST',
                headers: {
                    ...buildHeaders(context),
                    ...config.createHeaders,
                },
                body: payload,
            };
        },

        // 解析**任务创建**响应
        parseCreateTaskResponse(modelId, response, data): CreateTaskResult {
            const config = getModelConfig(modelId);
            const mapping = resolveStatusMapping(
                options.providerName, modelId, options.statusMapping, config.statusMapping
            );
            const taskId = getValueByPath(data, mapping.taskIdPath);
            if (!taskId) {
                const detail = [data?.message, data?.msg, typeof data?.error === 'string' ? data.error : undefined]
                    .find((value) => typeof value === 'string' && value);
                throw new Error(`Failed to get task id from response${detail ? `: ${detail}` : ''}`);
            }
            return {
                taskId: taskId,
                raw: data,
            };
        },

        // 构建**轮询任务**请求
        buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
            const config = getModelConfig(modelId);
            const pollApiPath = config.pollApiPath;
            return {
                url: (taskId) => {
                    const path = typeof pollApiPath === 'string'
                        ? resolvePollPath(pollApiPath, taskId)
                        : pollApiPath({ taskId, params });
                    return `${context.baseUrl}${path}`;
                },
                method: 'GET',
                headers: buildHeaders(context),
            };
        },

        // 解析**轮询任务**响应
        parsePollTaskResponse(modelId, response, data): PollTaskResult {
            const config = getModelConfig(modelId);
            const mapping = resolveStatusMapping(
                options.providerName, modelId, options.statusMapping, config.statusMapping
            );
            // 获取任务状态
            const state = getValueByPath(data, mapping.statePath);
            // 如果状态值未找到则视为**处理中**
            if (state === undefined) {
                return {
                    status: TaskStatus.PROCESSING,
                    raw: data,
                };
            }
            // 1. 判断**成功**状态
            if (mapping.success.length === 0) {
                // success 为空数组：改用 videoUrlPath 是否取到 URL 判断成功
                // （如 fal 队列结果端点：成功时仅返回输出载荷、无状态字段）
                const videoUrls = extractVideoUrls(data, mapping);
                if (videoUrls.length > 0) {
                    return {
                        status: TaskStatus.SUCCESS,
                        videoUrl: videoUrls[0],
                        ...(videoUrls.length > 1 ? { videoUrls } : {}),
                        raw: data,
                    };
                }
            } else if (isStatusMatch(state, mapping.success)) {
                const videoUrls = extractVideoUrls(data, mapping);
                const videoUrl = videoUrls[0];
                // 视频 URL 不存在则视为**失败**状态
                if (!videoUrl) {
                    return {
                        status: TaskStatus.FAILED,
                        error: 'Missing video URL in result',
                        raw: data,
                    };
                }
                // 返回**成功**状态（多视频时同时填充 videoUrls）
                return {
                    status: TaskStatus.SUCCESS,
                    videoUrl,
                    ...(videoUrls.length > 1 ? { videoUrls } : {}),
                    raw: data,
                };
            }
            // 2. 判断**失败**状态
            const error = getValueByPath(data, mapping.errorPath);
            if (mapping.failed.length === 0) {
                // failed 为空数组：改用 errorPath 字段是否有值来判断失败
                if (error !== undefined && error !== null && error !== '') {
                    return {
                        status: TaskStatus.FAILED,
                        error: String(error),
                        raw: data,
                    };
                }
            } else if (isStatusMatch(state, mapping.failed)) {
                return {
                    status: TaskStatus.FAILED,
                    error: error != null && error !== '' ? String(error) : 'unknown error',
                    raw: data,
                };
            }
            // 3. 判断**处理中**状态（含未知状态兜底）
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        },
    };

    // 工厂函数：按 modelId 创建 VideoModel
    return (modelId: string): VideoModel => {
        const config = getModelConfig(modelId);
        // 提前校验状态映射完整性，配置错误在注册期即可暴露
        resolveStatusMapping(options.providerName, modelId, options.statusMapping, config.statusMapping);
        const provider = getOrCreateProvider(
            config.baseUrlEnv ?? options.baseUrlEnv,
            config.apiKeyEnv ?? options.apiKeyEnv
        );
        return provider.createVideoModel(modelId, adapter);
    };
}
